import type { BattleActor } from '@mezha/api'
import catalogue from './roster.generated.json'
import accepted from '../../../../assets/combat-roster/accepted.json'
import pages from '../../../../assets/combat-roster/pages.generated.json'

export type Pose = 'idle' | 'prepare' | 'attack' | 'recover' | 'guard' | 'dodge' | 'hit' | 'cast' | 'rest' | 'stagger' | 'out' | 'breathe'
export const POSES: Pose[] = ['idle', 'prepare', 'attack', 'recover', 'guard', 'dodge', 'hit', 'cast', 'rest', 'stagger', 'out', 'breathe']
export type SourceFrame = { x: number; y: number; w: number; h: number; anchorX: number; anchorY: number; clips?: number[][] }
export type CombatArt = {
  key: string; url: string; columns: number; rows: number; facing: 1 | -1
  scale: number; ground: number; heightRatio?: number; frames: Record<Pose, number>
  extent?: { left: number; right: number; up: number; down: number }
  fitExtent?: { left: number; right: number; up: number; down: number }
  rectangles?: SourceFrame[]
}
const frames = Object.fromEntries(POSES.map((p, i) => [p, i])) as Record<Pose, number>
export const roster = catalogue
export function portraitHash(portrait: string) {
  const pathname = portrait.split(/[?#]/)[0]
  return pathname.split('/').pop()?.split('.')[0].toLowerCase() ?? ''
}
export function appearanceFor(actor: Pick<BattleActor, 'portrait'>) {
  return roster.find(entry => entry.hash === portraitHash(actor.portrait)) ?? null
}

/** Only visually inspected revisions belong here. Missing combinations remain explicit. */
export const releasedAtlases: Record<string, CombatArt> = Object.fromEntries(accepted.map(entry => {
  const page = (pages as Record<string, Pick<CombatArt, 'columns' | 'rows' | 'rectangles' | 'heightRatio' | 'extent' | 'scale'>>)[entry.key]
  const art = atlasDescriptor(entry.key, entry.scale)
  const overrides = entry as typeof entry & Partial<Record<`${Pose}Frame`, number>>
  const selected = Object.fromEntries(POSES.map(pose => [pose, pose === 'breathe' ? overrides.idleFrame ?? 0 : overrides[`${pose}Frame`] ?? art.frames[pose]])) as Record<Pose, number>
  return [`${entry.appearance}:${entry.variant}`, { ...art, ...page, frames: selected }]
}))

// Equipment and disarm may swap pages, but never the body's scale or fitting envelope.
// Avatars share a human scale; creatures retain their authored species proportions.
const fitGroups = new Map<string, NonNullable<CombatArt['extent']>>()
for (const entry of accepted) {
  const art = releasedAtlases[`${entry.appearance}:${entry.variant}`]
  if (!art.extent) continue
  const group = /^[fm]\./.test(entry.appearance) ? 'avatars' : entry.appearance
  const extent = fitGroups.get(group) ?? { left: 0, right: 0, up: 0, down: 0 }
  for (const edge of ['left', 'right', 'up', 'down'] as const) extent[edge] = Math.max(extent[edge], art.extent[edge] * art.scale)
  fitGroups.set(group, extent)
}
for (const entry of accepted) {
  releasedAtlases[`${entry.appearance}:${entry.variant}`].fitExtent = fitGroups.get(/^[fm]\./.test(entry.appearance) ? 'avatars' : entry.appearance)
}

export function atlasDescriptor(key: string, scale = 1): CombatArt {
  return { key, url: `/assets/combat/roster/${key}.webp`, columns: 4, rows: 3, facing: 1, scale, ground: .9, frames }
}
export function variantFor(actor: BattleActor) {
  const disarmed = actor.statuses.some(s => s.id === 'disarm')
  return `${disarmed ? 'unarmed' : actor.weapon_family || 'unarmed'}${actor.has_shield ? '-shield' : ''}`
}
export function resolveCombatArt(actor: BattleActor): CombatArt | null {
  const appearance = appearanceFor(actor)
  if (!appearance) return null
  const variant = variantFor(actor)
  // Bots can have NPC=true while using a player appearance; identity wins over actor category.
  const knownEnemyLoadout = roster.some(row => row.hash === appearance.hash && row.kind === 'enemy' && (actor.weapon_family || '') === (row.family || '') && Boolean(actor.has_shield) === Boolean(row.shield))
  if (knownEnemyLoadout && !actor.statuses.some(s => s.id === 'disarm')) {
    const natural = releasedAtlases[`${appearance.id}:natural${actor.has_shield ? '-shield' : ''}`]
    if (natural) return natural
  }
  return releasedAtlases[`${appearance.id}:${variant}`] ?? null
}

export function sourceFrame(art: CombatArt, pose: Pose, width: number, height: number): SourceFrame {
  const index = art.frames[pose]
  if (art.rectangles?.[index]) return art.rectangles[index]
  const w = width / art.columns, h = height / art.rows
  return { x: index % art.columns * w, y: Math.floor(index / art.columns) * h, w, h, anchorX: w / 2, anchorY: h * art.ground }
}

const contourPaths = new WeakMap<SourceFrame, Path2D>()
/** The same reviewed extraction geometry is used by the game and the pose viewer. */
export function paintFrame(c: CanvasRenderingContext2D, img: HTMLImageElement, frame: SourceFrame, x: number, y: number, scale: number, facing = 1) {
  c.save(); c.translate(x, y); c.scale(scale * facing, scale); c.translate(-frame.anchorX, -frame.anchorY)
  if (frame.clips) {
    let path = contourPaths.get(frame)
    if (!path) { path = new Path2D(); for (const [x, y, w, h] of frame.clips) path.rect(x, y, w, h); contourPaths.set(frame, path) }
    c.clip(path)
  }
  c.drawImage(img, frame.x, frame.y, frame.w, frame.h, 0, 0, frame.w, frame.h)
  c.restore()
}
